import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Star, MapPin, Calendar, Hammer, MessageSquare, Edit } from 'lucide-react';
import { useMutation, useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import ToolCard from '../components/features/ToolCard';
import Dialog from '../components/ui/Dialog';
import { useAuth } from '../context/AuthContext';
import { GET_PROFILE_BY_ID } from '../graphql/queries/profileById';
import { GET_USER_REVIEWS } from '../graphql/queries/userReviews';
import { CREATE_PROFILE_MESSAGE_THREAD } from '../graphql/mutations/createProfileMessageThread';
import { SEND_MESSAGE } from '../graphql/mutations/sendMessage';

const Profile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const urlTab = searchParams.get('tab');
  const initialTab: 'tools' | 'reviews' = urlTab === 'reviews' ? 'reviews' : 'tools';
  const [activeTab, setActiveTab] = useState<'tools' | 'reviews'>(initialTab);
  const [toolsPage, setToolsPage] = useState(1);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [useLightHeroText, setUseLightHeroText] = useState(true);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [messageError, setMessageError] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const ITEMS_PER_PAGE = 6;
  const currentDate = new Date().toISOString().split('T')[0];
  const profileId = id || user?.id || null;
  const isOwnProfile = Boolean(user?.id && profileId === user.id);

  const { data: profileData, loading: profileLoading } = useQuery(GET_PROFILE_BY_ID, {
    skip: !profileId,
    variables: {
      id: profileId,
      limit: ITEMS_PER_PAGE,
      offset: (toolsPage - 1) * ITEMS_PER_PAGE,
      date: currentDate,
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const { data: reviewsData, loading: reviewsLoading } = useQuery(GET_USER_REVIEWS, {
    skip: !profileId || activeTab !== 'reviews',
    variables: {
      id: profileId,
      limit: ITEMS_PER_PAGE,
      offset: (reviewsPage - 1) * ITEMS_PER_PAGE,
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const queriedProfile = profileData?.user || null;
  const otherProfile = queriedProfile || null;
  const isProfileLoading = profileLoading && !otherProfile;

  const profile = otherProfile;
  const canMessageUser = Boolean(user?.id && profile?.id && !isOwnProfile);
  const displayTools = profile?.toolsList?.items || [];
  const toolsTotal = profile?.toolsList?.totalCount ?? 0;
  const toolsTotalPages = Math.max(1, Math.ceil(toolsTotal / ITEMS_PER_PAGE));
  const reviews = reviewsData?.user?.reviewsList?.items || [];
  const reviewsTotal = profile?.reviewCount ?? 0;
  const reviewsTotalPages = Math.max(1, Math.ceil(reviewsTotal / ITEMS_PER_PAGE));

  const { name, about, ratingAverage, avatarImageUrl, city, postalCode } = profile || {};
  const reviewCount = profile?.reviewCount ?? 0;
  const toolCount = profile?.toolCount ?? toolsTotal;
  const userCreatedAt = profile?.createdAt ? new Date(profile.createdAt) : null;
  const locationText = [postalCode, city].filter(Boolean).join(' ') || 'Lokation ikke angivet';
  const heroBannerImage = displayTools.find((tool: any) => tool?.primaryImageUrl)?.primaryImageUrl || '';

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'tools' || tab === 'reviews') {
      setActiveTab(tab);
      return;
    }
    setActiveTab('tools');
  }, [searchParams]);

  useEffect(() => {
    if (!heroBannerImage) {
      setUseLightHeroText(true);
      return;
    }

    let isCancelled = false;
    const image = new Image();
    image.crossOrigin = 'anonymous';

    image.onload = () => {
      if (isCancelled) return;

      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        const context = canvas.getContext('2d');
        if (!context) {
          setUseLightHeroText(true);
          return;
        }

        context.drawImage(image, 0, 0);

        const sampleX = Math.floor(image.width * 0.16);
        const sampleY = Math.floor(image.height * 0.60);
        const sampleWidth = Math.max(1, Math.floor(image.width * 0.28));
        const sampleHeight = Math.max(1, Math.floor(image.height * 0.22));

        const pixels = context.getImageData(sampleX, sampleY, sampleWidth, sampleHeight).data;
        let luminanceTotal = 0;
        const pixelCount = pixels.length / 4;

        for (let index = 0; index < pixels.length; index += 4) {
          const red = pixels[index];
          const green = pixels[index + 1];
          const blue = pixels[index + 2];
          luminanceTotal += (0.2126 * red) + (0.7152 * green) + (0.0722 * blue);
        }

        const averageLuminance = luminanceTotal / Math.max(1, pixelCount);
        setUseLightHeroText(averageLuminance < 145);
      } catch {
        setUseLightHeroText(true);
      }
    };

    image.onerror = () => {
      if (!isCancelled) {
        setUseLightHeroText(true);
      }
    };

    image.src = heroBannerImage;

    return () => {
      isCancelled = true;
    };
  }, [heroBannerImage]);

  const [createProfileMessageThread] = useMutation(CREATE_PROFILE_MESSAGE_THREAD);
  const [sendMessage] = useMutation(SEND_MESSAGE);

  const mapProfileMessageError = (errorText: string) => {
    const normalized = errorText.trim().toLowerCase();

    if (normalized === 'not authenticated') {
      return 'Du skal være logget ind for at sende en besked.';
    }
    if (normalized === 'user not found') {
      return 'Brugeren blev ikke fundet.';
    }
    if (normalized === 'cannot message yourself') {
      return 'Du kan ikke sende besked til dig selv.';
    }

    return errorText;
  };

  const closeMessageDialog = () => {
    setIsMessageDialogOpen(false);
    setMessageError('');
    setMessageContent('');
  };

  const handleTabChange = (tab: 'tools' | 'reviews') => {
    setActiveTab(tab);
    const nextSearchParams = new URLSearchParams(searchParams);

    if (tab === 'tools') {
      nextSearchParams.delete('tab');
    } else {
      nextSearchParams.set('tab', tab);
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const handleSendProfileMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessageError('');

    const trimmedMessage = messageContent.trim();
    if (!trimmedMessage) {
      setMessageError('Skriv en besked, før du sender.');
      return;
    }

    if (!profile?.id) {
      setMessageError('Kunne ikke finde brugerprofilen.');
      return;
    }

    setIsSendingMessage(true);

    try {
      const { data: threadData } = await createProfileMessageThread({
        variables: { userId: profile.id },
      });

      const threadErrors = threadData?.createProfileMessageThread?.errors || [];
      if (threadErrors.length > 0) {
        setMessageError(mapProfileMessageError(threadErrors[0]));
        return;
      }

      const threadId = threadData?.createProfileMessageThread?.messageThread?.id;
      if (!threadId) {
        setMessageError('Kunne ikke oprette eller hente en beskedtråd.');
        return;
      }

      const { data: messageData } = await sendMessage({
        variables: {
          threadId,
          content: trimmedMessage,
        },
      });

      const messageErrors = messageData?.createMessage?.errors || [];
      if (messageErrors.length > 0) {
        setMessageError(mapProfileMessageError(messageErrors[0]));
        return;
      }

      toast.success('Besked sendt');
      closeMessageDialog();
      navigate(`/conversation/${threadId}`);
    } catch {
      setMessageError('Der opstod en fejl ved afsendelse af beskeden. Prøv igen.');
    } finally {
      setIsSendingMessage(false);
    }
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('da-DK', options);
  };

  if (isProfileLoading) {
    return (
      <div className="container mx-auto px-4 pt-4 pb-8 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          Indlæser profil...
        </div>
      </div>
    );
  }

  if (!isOwnProfile && !profile) {
    return (
      <div className="container mx-auto px-4 pt-4 pb-8 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <h1 className="text-xl font-semibold text-gray-900">Profil ikke fundet</h1>
          <p className="mt-2 text-gray-600">Brugeren findes ikke, eller kunne ikke indlæses lige nu.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 pt-4 pb-8 sm:px-6 lg:px-8">
      {/* Profile Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
        <div className="relative h-32 sm:h-48 bg-green-600">
          {isOwnProfile && (
            <Link
              to="/settings"
              className="sm:hidden absolute top-3 right-3 z-20"
              aria-label="Rediger profil"
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/80 bg-white/95 text-gray-900 shadow-sm hover:bg-white">
                <Edit className="h-4 w-4" />
              </span>
            </Link>
          )}

          {heroBannerImage && (
            <img
              src={heroBannerImage}
              alt="Profil banner"
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
          {heroBannerImage && (
            <div className={`absolute inset-0 ${useLightHeroText ? 'bg-green-900/45' : 'bg-white/20'}`}></div>
          )}
          <div className="absolute inset-x-0 bottom-4 px-4 sm:px-6 lg:px-8 z-10">
            <div className="ml-0 sm:ml-[152px] flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h1 className={`hidden sm:block text-2xl font-bold leading-tight ${useLightHeroText ? 'text-white' : 'text-gray-900'}`}>{name}</h1>
              {isOwnProfile && (
                <div className="mt-3 sm:mt-0 hidden sm:block">
                  <Link to="/settings">
                    <Button
                      variant="outline"
                      icon={<Edit className="h-4 w-4 mr-2" />}
                      className="bg-white/95 border-white/80 text-gray-900 hover:bg-white"
                    >
                      Rediger profil
                    </Button>
                  </Link>
                </div>
              )}
              {canMessageUser && (
                <div className="mt-3 sm:mt-0 hidden md:block">
                  <Button
                    variant="primary"
                    icon={<MessageSquare className="h-4 w-4 mr-2" />}
                    onClick={() => setIsMessageDialogOpen(true)}
                  >
                    Send besked
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-end">
            <div className="-mt-16 sm:-mt-16 h-24 w-24 sm:h-32 sm:w-32 rounded-full border-4 border-white bg-white flex items-center justify-center text-green-600 font-medium overflow-hidden relative z-10">
              {avatarImageUrl ? (
                <img src={avatarImageUrl} alt={name} className="h-full w-full object-cover" />
              ) : (
                <span className="text-4xl">{name?.charAt(0).toUpperCase() || "U"}</span>
              )}
            </div>
            <div className="sm:ml-6 mt-4 sm:mt-0 flex-1">
              <h1 className="sm:hidden text-2xl font-bold text-gray-900 leading-tight break-words">{name}</h1>

              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-8">
                <div className="flex items-center min-w-0">
                  <MapPin className="h-4 w-4 text-gray-500 mr-1 flex-shrink-0" />
                  <span className="text-sm text-gray-600 truncate">{locationText}</span>
                </div>
                <div className="flex items-center min-w-0">
                  <Calendar className="h-4 w-4 text-gray-500 mr-1 flex-shrink-0" />
                  <span className="text-sm text-gray-600 truncate">Medlem siden {userCreatedAt?.toLocaleString('da-DK', { month: 'long' })} {userCreatedAt?.getFullYear()}</span>
                </div>
              </div>
              <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-8">
                <div className="flex items-center">
                  <Star className="h-5 w-5 text-yellow-500 fill-current" />
                  <span className="ml-1 font-medium text-gray-900">{(ratingAverage || 0).toFixed(1)}</span>
                  <span className="ml-1 text-sm text-gray-500">({reviewCount} anmeldelser)</span>
                </div>
                <div className="flex items-center">
                  <Hammer className="h-5 w-5 text-gray-500 mr-1" />
                  <span className="font-medium text-gray-900">{toolCount}</span>
                  <span className="ml-1 text-sm text-gray-500">værktøj</span>
                </div>
                {/* <div className="flex items-center">
                  <Shield className="h-5 w-5 text-gray-500 mr-1" />
                  <span className="text-sm text-gray-600">Identity verified</span>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* About Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">Om</h2>
          {/* <Button variant="outline" size="sm" icon={<Edit className="h-4 w-4 mr-2" />}>
            Edit
          </Button> */}
        </div>
        <p className="text-gray-700">
          {about}
        </p>
      </div>
      
      {/* Tabs Navigation */}
      <div className="border-b border-gray-200 mb-8">
        <div className="flex space-x-8">
          <button
            onClick={() => handleTabChange('tools')}
            className={`pb-4 -mb-px text-sm font-medium ${
              activeTab === 'tools'
                ? 'text-green-600 border-b-2 border-green-600'
                : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
            }`}
          >
            <span>Værktøj</span>
            <span className="ml-2 bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 text-xs">
              {toolCount}
            </span>
          </button>
          
          <button
            onClick={() => handleTabChange('reviews')}
            className={`pb-4 -mb-px text-sm font-medium ${
              activeTab === 'reviews'
                ? 'text-green-600 border-b-2 border-green-600'
                : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
            }`}
          >
            <span>Anmeldelser</span>
            <span className="ml-2 bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 text-xs">
              {reviewCount}
            </span>
          </button>
        </div>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'tools' ? (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">Værktøj ({toolsTotal})</h2>
            {/* <Link to="/add-tool">
              <Button variant="primary">Opret værktøj</Button>
            </Link> */}
          </div>
          
          {displayTools.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayTools.map((tool: any) => (
                  <ToolCard
                    key={tool.id}
                    tool={{
                      ...tool,
                      categories: [],
                      city: city || '',
                      postalCode: postalCode || '',
                      rating: tool.ratingAverage || 0,
                      reviews: [],
                      availability: '',
                    }}
                  />
                ))}
              </div>

              {toolsTotalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-500">
                    Viser {((toolsPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(toolsPage * ITEMS_PER_PAGE, toolsTotal)} af {toolsTotal}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setToolsPage((p) => Math.max(1, p - 1))}
                      disabled={toolsPage === 1}
                      className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Forrige
                    </button>
                    <span className="text-sm text-gray-700">
                      Side {toolsPage} af {toolsTotalPages}
                    </span>
                    <button
                      onClick={() => setToolsPage((p) => Math.min(toolsTotalPages, p + 1))}
                      disabled={toolsPage === toolsTotalPages}
                      className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Næste
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <Hammer className="h-12 w-12" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Intet værktøj oprettet</h3>
              <p className="mt-1 text-sm text-gray-500">
                {isOwnProfile
                  ? 'Kom i gang ved at oprette dit første værktøj.'
                  : 'Denne bruger har ikke oprettet værktøj endnu.'}
              </p>
              {isOwnProfile && (
                <div className="mt-6">
                  <Link to="/add-tool">
                    <Button variant="primary">Opret værktøj</Button>
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900">Anmeldelser ({reviewsTotal})</h2>
          </div>

          {reviewsLoading ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
              <p className="text-gray-500">Indlæser anmeldelser...</p>
            </div>
          ) : reviews.length > 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {reviews.map((review: any, index: number) => (
                <div 
                  key={review.id}
                  className={`p-6 ${
                    index !== reviews.length - 1 ? 'border-b border-gray-200' : ''
                  }`}
                >
                  <div className="flex items-start">
                    <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 font-medium overflow-hidden mr-4">
                      {review.createdByUser?.avatarImageUrl ? (
                        <img
                          src={review.createdByUser.avatarImageUrl}
                          alt={review.createdByUser?.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span>{review.createdByUser?.name?.charAt(0).toUpperCase() || 'U'}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:justify-between">
                        <h3 className="text-sm font-medium text-gray-900">{review.createdByUser?.name || 'Ukendt bruger'}</h3>
                        <p className="text-sm text-gray-500 sm:text-right">{formatDate(review.createdAt)}</p>
                      </div>
                      <div className="flex text-yellow-500 my-1">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <Star 
                            key={rating}
                            className={`h-4 w-4 ${
                              rating <= review.rating ? 'fill-current' : ''
                            }`} 
                          />
                        ))}
                      </div>
                      {review.tool?.name && (
                        <p className="text-xs text-gray-500">for {review.tool.name}</p>
                      )}
                      <p className="text-gray-700 mt-2">{review.comment}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <Star className="h-12 w-12" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Ingen anmeldelser endnu</h3>
              <p className="mt-1 text-sm text-gray-500">
                {isOwnProfile
                  ? 'Anmeldelser vises her, når nogen har lånt dit værktøj.'
                  : 'Denne bruger har ikke anmeldelser endnu.'}
              </p>
            </div>
          )}

          {reviewsTotalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                Viser {((reviewsPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(reviewsPage * ITEMS_PER_PAGE, reviewsTotal)} af {reviewsTotal}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setReviewsPage((p) => Math.max(1, p - 1))}
                  disabled={reviewsPage === 1}
                  className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Forrige
                </button>
                <span className="text-sm text-gray-700">
                  Side {reviewsPage} af {reviewsTotalPages}
                </span>
                <button
                  onClick={() => setReviewsPage((p) => Math.min(reviewsTotalPages, p + 1))}
                  disabled={reviewsPage === reviewsTotalPages}
                  className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Næste
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Contact Button - Fixed at Bottom on Mobile */}
      {canMessageUser && (
        <div className="fixed bottom-4 left-0 right-0 md:hidden flex justify-center px-4 z-10">
          <Button
            variant="primary"
            fullWidth
            className="shadow-lg"
            icon={<MessageSquare className="h-4 w-4 mr-2" />}
            onClick={() => setIsMessageDialogOpen(true)}
          >
            Send besked
          </Button>
        </div>
      )}

      <Dialog
        isOpen={isMessageDialogOpen}
        onClose={closeMessageDialog}
        title={`Send besked til ${name || 'bruger'}`}
        size="sm"
      >
        <form onSubmit={handleSendProfileMessage} className="p-6 space-y-4">
          <div>
            <label htmlFor="profile-message" className="block text-sm font-medium text-gray-700 mb-1">
              Besked
            </label>
            <textarea
              id="profile-message"
              rows={5}
              value={messageContent}
              onChange={(event) => setMessageContent(event.target.value)}
              placeholder="Skriv din besked her..."
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          {messageError && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {messageError}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeMessageDialog}>
              Annuller
            </Button>
            <Button type="submit" variant="primary" isLoading={isSendingMessage}>
              Send besked
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
};

export default Profile;